<template>
  <div class="seo-site-detail-page" v-loading="loading">
    <div class="seo-site-detail-page__header">
      <div>
        <el-button size="small" icon="el-icon-arrow-left" @click="backToSites">
          返回站点管理
        </el-button>
        <div class="seo-site-detail-page__title">
          {{ siteDetail.site_name || "站点详情" }}
          <span v-if="siteDetail.site_url">/ {{ siteDetail.site_url }}</span>
        </div>
        <div class="seo-site-detail-page__desc">
          单站点关键词结构、达标状态与最新排名快照
        </div>
      </div>
      <div class="seo-site-detail-page__actions">
        <el-radio-group v-model="range" size="small">
          <el-radio-button
            v-for="item in timeRangeOptions"
            :key="item.value"
            :label="item.value"
          >
            {{ item.label }}
          </el-radio-button>
        </el-radio-group>
        <el-date-picker
          v-if="range === 'custom'"
          v-model="customTime"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          size="small"
        ></el-date-picker>
        <el-button size="small" icon="el-icon-refresh" @click="refresh">
          刷新
        </el-button>
      </div>
    </div>

    <div class="seo-site-detail-page__info-panel">
      <div class="seo-site-detail-page__info-item">
        <span>站点ID</span>
        <strong>{{ siteDetail.site_id || "-" }}</strong>
      </div>
      <div class="seo-site-detail-page__info-item">
        <span>承诺关键词</span>
        <strong>{{ siteDetail.committed_keyword_count || 0 }}</strong>
      </div>
      <div class="seo-site-detail-page__info-item">
        <span>承诺排名范围</span>
        <strong>{{
          committedRankLimitText(siteDetail.committed_rank_limit)
        }}</strong>
      </div>
      <div class="seo-site-detail-page__info-item">
        <span>SEO状态</span>
        <el-tag
          size="small"
          :type="siteDetail.site_status === 1 ? 'success' : 'info'"
        >
          {{ siteDetail.site_status === 1 ? "启用" : "停用" }}
        </el-tag>
      </div>
      <div class="seo-site-detail-page__info-item">
        <span>数据口径</span>
        <strong>{{ currentRangeLabel }}</strong>
      </div>
    </div>

    <div class="seo-site-detail-page__stats">
      <div
        v-for="card in metricCards"
        :key="card.key"
        class="seo-site-detail-page__stat-card"
      >
        <div
          :class="[
            'seo-site-detail-page__stat-icon',
            `seo-site-detail-page__stat-icon--${card.tone}`,
          ]"
        >
          <i :class="card.icon"></i>
        </div>
        <div>
          <div class="seo-site-detail-page__stat-label">{{ card.label }}</div>
          <div class="seo-site-detail-page__stat-value">{{ card.value }}</div>
          <div class="seo-site-detail-page__stat-extra">{{ card.extra }}</div>
        </div>
      </div>
    </div>

    <div class="seo-site-detail-page__grid">
      <div class="seo-site-detail-page__panel">
        <div class="seo-site-detail-page__panel-head">
          <div>
            <div class="seo-site-detail-page__panel-title">排名状态分布</div>
            <div class="seo-site-detail-page__panel-note">
              达标、未达标与未找到关键词
            </div>
          </div>
        </div>
        <div ref="statusChart" class="seo-site-detail-page__chart"></div>
      </div>
      <div class="seo-site-detail-page__panel">
        <div class="seo-site-detail-page__panel-head">
          <div>
            <div class="seo-site-detail-page__panel-title">关键词类型分布</div>
            <div class="seo-site-detail-page__panel-note">
              该站点关键词业务类型
            </div>
          </div>
        </div>
        <div ref="typeChart" class="seo-site-detail-page__chart"></div>
      </div>
    </div>

    <div class="seo-site-detail-page__panel">
      <div class="seo-site-detail-page__panel-head">
        <div>
          <div class="seo-site-detail-page__panel-title">关键词明细</div>
          <div class="seo-site-detail-page__panel-note">
            当前站点全部关键词与最近一次排名快照
          </div>
        </div>
      </div>
      <el-table
        :data="pager.list"
        empty-text="暂无关键词数据"
        tooltip-effect="light"
        class="seo-site-detail-page__keyword-table"
      >
        <el-table-column prop="id" label="ID" width="58"></el-table-column>
        <el-table-column label="关键词" min-width="150">
          <template v-slot="scope">
            <button
              v-if="scope.row.keyword"
              class="seo-site-detail-page__copy-cell"
              type="button"
              @click="copyText(scope.row.keyword)"
            >
              {{ scope.row.keyword }}
            </button>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="类型" width="92">
          <template v-slot="scope">
            <el-tag size="small" type="info">
              {{ keywordTypeText(scope.row.keyword_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="目标域名" width="132">
          <template v-slot="scope">
            <button
              v-if="scope.row.target_domain"
              class="seo-site-detail-page__copy-cell"
              type="button"
              @click="copyText(scope.row.target_domain)"
            >
              {{ scope.row.target_domain }}
            </button>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="参与" width="74">
          <template v-slot="scope">
            <el-tag size="small" v-if="scope.row.include_in_rank_check === 1"
              >参与</el-tag
            >
            <el-tag size="small" type="info" v-else>不参与</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="最新排名" width="82">
          <template v-slot="scope">
            <span v-if="scope.row.latest_rank_position">{{
              scope.row.latest_rank_position
            }}</span>
            <span v-else-if="scope.row.latest_checked_at">
              前{{ scope.row.latest_rank_depth || 50 }}未找到
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="达标" width="80">
          <template v-slot="scope">
            <el-tag
              size="small"
              :type="Number(scope.row.is_qualified) === 1 ? 'success' : 'info'"
            >
              {{ Number(scope.row.is_qualified) === 1 ? "达标" : "未达标" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="变化" width="70">
          <template v-slot="scope">
            <span v-if="scope.row.rank_change === null">-</span>
            <span
              v-else-if="scope.row.rank_change > 0"
              class="seo-site-detail-page__rank-up"
              >+{{ scope.row.rank_change }}</span
            >
            <span
              v-else-if="scope.row.rank_change < 0"
              class="seo-site-detail-page__rank-down"
              >{{ scope.row.rank_change }}</span
            >
            <span v-else>0</span>
          </template>
        </el-table-column>
        <el-table-column label="检查时间" width="126">
          <template v-slot="scope">
            <div
              v-if="scope.row.latest_checked_at"
              class="seo-site-detail-page__datetime"
            >
              <span>{{
                formatDate(scope.row.latest_checked_at, "yyyy-mm-dd")
              }}</span>
              <span>{{
                formatDate(scope.row.latest_checked_at, "hh:MM:ss")
              }}</span>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="快照状态" width="100">
          <template v-slot="scope">
            <el-tag
              size="small"
              :type="rankStatusTagType(keywordRankStatus(scope.row))"
            >
              {{ rankStatusText(keywordRankStatus(scope.row), scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="深度" width="66">
          <template v-slot="scope">
            {{ scope.row.latest_rank_depth || "-" }}
          </template>
        </el-table-column>
        <el-table-column label="快照URL" min-width="170">
          <template v-slot="scope">
            <el-popover
              v-if="scope.row.latest_ranking_url"
              trigger="manual"
              placement="top"
              width="420"
              :value="urlPopoverKey === scope.row.id"
              popper-class="seo-site-detail-page__url-popper"
            >
              <div
                class="seo-site-detail-page__url-popover"
                @mouseenter="keepUrlPopover"
                @mouseleave="scheduleUrlPopoverClose"
              >
                <div class="seo-site-detail-page__url-popover-text">
                  {{ scope.row.latest_ranking_url }}
                </div>
                <el-button
                  type="text"
                  size="mini"
                  @click="copyText(scope.row.latest_ranking_url)"
                >
                  复制URL
                </el-button>
              </div>
              <button
                slot="reference"
                class="seo-site-detail-page__copy-cell"
                type="button"
                @mouseenter="showUrlPopover(scope.row)"
                @mouseleave="scheduleUrlPopoverClose"
                @click="copyText(scope.row.latest_ranking_url)"
              >
                {{ scope.row.latest_ranking_url }}
              </button>
            </el-popover>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="132">
          <template v-slot="scope">
            <el-button type="text" @click="openSnapshots(scope.row)">
              快照历史
            </el-button>
            <el-button
              v-if="$hasPermission('seo.keyword', 'edit')"
              type="text"
              :loading="liveRankLoadingId === scope.row.id"
              @click="onLiveRank(scope.row)"
            >
              Live诊断
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="seo-site-detail-page__pagination">
        <pagination
          v-model="pager"
          :pageSizes="[10, 20, 30, 50, 100, 200]"
          @change="getKeywords()"
        ></pagination>
      </div>
    </div>

    <el-drawer
      :visible.sync="snapshotDrawer.visible"
      title="排名快照历史"
      size="920px"
      append-to-body
    >
      <div class="seo-site-detail-page__snapshot-drawer">
        <div class="seo-site-detail-page__snapshot-title">
          {{ snapshotDrawer.row.keyword || "-" }}
        </div>
        <el-table
          :data="snapshotPager.list"
          v-loading="snapshotPager.loading"
          empty-text="暂无快照历史"
        >
          <el-table-column label="状态" width="90">
            <template v-slot="scope">
              <el-tag
                size="small"
                :type="rankStatusTagType(scope.row.rank_status)"
              >
                {{ rankStatusText(scope.row.rank_status, scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="排名" width="80">
            <template v-slot="scope">
              <span v-if="scope.row.rank_position !== null">
                {{ scope.row.rank_position }}
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="变化" width="80">
            <template v-slot="scope">
              <span v-if="scope.row.ranking_change === null">-</span>
              <span
                v-else-if="scope.row.ranking_change > 0"
                class="seo-site-detail-page__rank-up"
                >+{{ scope.row.ranking_change }}</span
              >
              <span
                v-else-if="scope.row.ranking_change < 0"
                class="seo-site-detail-page__rank-down"
                >{{ scope.row.ranking_change }}</span
              >
              <span v-else>0</span>
            </template>
          </el-table-column>
          <el-table-column label="排名 URL" min-width="320">
            <template v-slot="scope">
              <div
                v-if="scope.row.ranking_url"
                class="seo-site-detail-page__ranking-url"
              >
                <span>{{ scope.row.ranking_url }}</span>
                <el-button
                  type="text"
                  size="mini"
                  @click="copyText(scope.row.ranking_url)"
                >
                  复制
                </el-button>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="检查时间" width="116">
            <template v-slot="scope">
              <div
                v-if="scope.row.checked_at"
                class="seo-site-detail-page__datetime"
              >
                <span>{{
                  formatDate(scope.row.checked_at, "yyyy-mm-dd")
                }}</span>
                <span>{{ formatDate(scope.row.checked_at, "hh:MM:ss") }}</span>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="深度" width="70">
            <template v-slot="scope">
              {{ scope.row.depth || "-" }}
            </template>
          </el-table-column>
          <el-table-column label="cost" width="100">
            <template v-slot="scope">
              <span v-if="scope.row.cost !== null">
                {{ formatCost(scope.row.cost) }}
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
        <div class="seo-site-detail-page__pagination">
          <pagination
            v-model="snapshotPager"
            :pageSizes="[10, 20, 30]"
            @change="getSnapshots()"
          ></pagination>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import Pagination from "@/components/pagination";
import { Pager } from "@/utils/pager";
import { formatDate } from "@/utils/util";
import {
  apiSeoKeywordList,
  apiSeoKeywordSiteSummary,
  apiSeoReportSnapshots,
  apiSeoSettingLiveRank,
} from "@/api/seo";
import * as echarts from "echarts/core";
import {
  GraphicComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import { PieChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";

echarts.use([
  GraphicComponent,
  LegendComponent,
  TooltipComponent,
  PieChart,
  CanvasRenderer,
]);

export default {
  components: {
    Pagination,
  },
  data() {
    return {
      range: "30d",
      customTime: [],
      timeRangeOptions: [
        { label: "今日", value: "today" },
        { label: "近7天", value: "7d" },
        { label: "近30天", value: "30d" },
        { label: "近半年", value: "180d" },
        { label: "近一年", value: "365d" },
        { label: "自定义", value: "custom" },
      ],
      siteDetail: {},
      pager: new Pager(),
      snapshotPager: new Pager(),
      snapshotDrawer: {
        visible: false,
        row: {},
      },
      liveRankLoadingId: 0,
      urlPopoverKey: 0,
      urlPopoverCloseTimer: null,
      charts: {},
      siteLoading: false,
    };
  },
  computed: {
    siteId() {
      return Number(this.$route.params.siteId || 0);
    },
    loading() {
      return this.siteLoading || this.pager.loading;
    },
    rows() {
      return this.pager.list || [];
    },
    metricCards() {
      const notFound = Math.max(
        0,
        Number(this.siteDetail.rank_check_count || 0) -
          Number(this.siteDetail.found_count || 0)
      );
      return [
        {
          key: "keyword",
          label: "关键词总数",
          value: this.siteDetail.keyword_count || 0,
          extra: "当前站点关键词",
          icon: "el-icon-collection-tag",
          tone: "blue",
        },
        {
          key: "rank",
          label: "参与排名",
          value: this.siteDetail.rank_check_count || 0,
          extra: "启用排名检查",
          icon: "el-icon-data-line",
          tone: "green",
        },
        {
          key: "found",
          label: "已找到",
          value: this.siteDetail.found_count || 0,
          extra: "最近快照命中",
          icon: "el-icon-position",
          tone: "yellow",
        },
        {
          key: "qualified",
          label: "达标数",
          value: this.siteDetail.qualified_count || 0,
          extra: "满足承诺排名",
          icon: "el-icon-success",
          tone: "purple",
        },
        {
          key: "rate",
          label: "达成率",
          value: this.siteDetail.achievement_rate || "0.00%",
          extra: "达标 / 承诺",
          icon: "el-icon-pie-chart",
          tone: "red",
        },
        {
          key: "not_found",
          label: "未找到",
          value: notFound,
          extra: "参与排名但未命中",
          icon: "el-icon-warning-outline",
          tone: "cyan",
        },
      ];
    },
    currentRangeLabel() {
      if (
        this.range === "custom" &&
        this.customTime &&
        this.customTime.length === 2
      ) {
        return `${this.customTime[0]} 至 ${this.customTime[1]}`;
      }
      const item = this.timeRangeOptions.find(
        (option) => option.value === this.range
      );
      return item ? item.label : "近30天";
    },
  },
  watch: {
    rows() {
      this.$nextTick(this.renderCharts);
    },
    siteDetail() {
      this.$nextTick(this.renderCharts);
    },
  },
  created() {
    this.refresh();
  },
  mounted() {
    window.addEventListener("resize", this.resizeCharts);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCharts);
    this.clearUrlPopoverTimer();
    Object.values(this.charts).forEach((chart) => chart.dispose());
  },
  methods: {
    formatDate,
    refresh() {
      if (!this.siteId) {
        this.backToSites();
        return;
      }
      this.getSiteDetail();
      this.getKeywords();
    },
    getSiteDetail() {
      this.siteLoading = true;
      apiSeoKeywordSiteSummary({ site_id: this.siteId, page: 1, size: 1 })
        .then((resp) => {
          this.siteDetail = (resp.list || [])[0] || { site_id: this.siteId };
        })
        .finally(() => {
          this.siteLoading = false;
        });
    },
    getKeywords(page) {
      page && (this.pager.page = page);
      this.pager.request({
        callback: apiSeoKeywordList,
        params: { site_id: this.siteId },
      });
    },
    getSnapshots(page) {
      page && (this.snapshotPager.page = page);
      this.snapshotPager.request({
        callback: apiSeoReportSnapshots,
        params: {
          seo_keyword_id: this.snapshotDrawer.row.id,
        },
      });
    },
    openSnapshots(row) {
      this.snapshotDrawer.row = row;
      this.snapshotDrawer.visible = true;
      this.getSnapshots(1);
    },
    showUrlPopover(row) {
      this.clearUrlPopoverTimer();
      this.urlPopoverKey = row.id;
    },
    keepUrlPopover() {
      this.clearUrlPopoverTimer();
    },
    scheduleUrlPopoverClose() {
      this.clearUrlPopoverTimer();
      this.urlPopoverCloseTimer = setTimeout(() => {
        this.urlPopoverKey = 0;
        this.urlPopoverCloseTimer = null;
      }, 180);
    },
    clearUrlPopoverTimer() {
      if (this.urlPopoverCloseTimer) {
        clearTimeout(this.urlPopoverCloseTimer);
        this.urlPopoverCloseTimer = null;
      }
    },
    onLiveRank(row) {
      this.$confirm(
        `将对关键词“${row.keyword}”执行Live手动诊断，可能产生DataForSEO费用。手动诊断不会完全打乱自动查询周期，系统会按查询间隔自动避免短时间重复查询。是否继续？`,
        "确认Live手动诊断",
        { type: "warning" }
      ).then(() => {
        this.liveRankLoadingId = row.id;
        apiSeoSettingLiveRank({ seo_keyword_id: row.id })
          .then((resp) => {
            const rank = resp.rank_position
              ? `，排名：${resp.rank_position}`
              : "";
            this.$message({
              type: "success",
              message: `Live诊断完成：${
                resp.found ? "已找到" : "未找到"
              }${rank}`,
            });
            this.getKeywords();
            this.getSiteDetail();
          })
          .finally(() => {
            this.liveRankLoadingId = 0;
          });
      });
    },
    backToSites() {
      this.$router.push("/seo/sites");
    },
    chart(ref) {
      if (!this.$refs[ref]) return null;
      if (!this.charts[ref]) {
        this.charts[ref] = echarts.init(this.$refs[ref]);
      }
      return this.charts[ref];
    },
    renderCharts() {
      this.renderStatusChart();
      this.renderTypeChart();
      this.resizeCharts();
    },
    renderStatusChart() {
      const chart = this.chart("statusChart");
      if (!chart) return;
      const found = Number(this.siteDetail.found_count || 0);
      const qualified = Number(this.siteDetail.qualified_count || 0);
      const notFound = Math.max(
        0,
        Number(this.siteDetail.rank_check_count || 0) - found
      );
      const data = [
        { name: "达标", value: qualified },
        { name: "未达标", value: Math.max(0, found - qualified) },
        { name: "未找到", value: notFound },
      ].filter((item) => item.value > 0);
      this.renderPie(chart, data, "暂无排名状态数据", [
        "#34a853",
        "#fbbc04",
        "#ea4335",
      ]);
    },
    renderTypeChart() {
      const chart = this.chart("typeChart");
      if (!chart) return;
      const data = [
        { name: "主词", value: Number(this.siteDetail.core_count || 0) },
        { name: "长尾词", value: Number(this.siteDetail.long_tail_count || 0) },
        { name: "品牌词", value: Number(this.siteDetail.brand_count || 0) },
        {
          name: "竞品词",
          value: Number(this.siteDetail.competitor_count || 0),
        },
      ].filter((item) => item.value > 0);
      this.renderPie(chart, data, "暂无关键词类型数据", [
        "#1a73e8",
        "#34a853",
        "#fbbc04",
        "#ea4335",
      ]);
    },
    renderPie(chart, data, emptyText, colors) {
      if (!data.length) {
        chart.setOption(this.emptyChartOption(emptyText), true);
        return;
      }
      chart.setOption(
        {
          color: colors,
          tooltip: { trigger: "item" },
          legend: {
            bottom: 0,
            icon: "circle",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: { color: "#5f6368" },
          },
          series: [
            {
              type: "pie",
              radius: ["48%", "72%"],
              center: ["50%", "44%"],
              itemStyle: { borderColor: "#fff", borderWidth: 3 },
              label: { color: "#3c4043" },
              data,
            },
          ],
        },
        true
      );
    },
    emptyChartOption(text) {
      return {
        graphic: {
          type: "text",
          left: "center",
          top: "middle",
          style: { text, fill: "#9aa0a6", fontSize: 13 },
        },
      };
    },
    resizeCharts() {
      Object.values(this.charts).forEach((chart) => chart.resize());
    },
    keywordTypeText(type) {
      return (
        {
          core: "主词",
          long_tail: "长尾词",
          brand: "品牌词",
          competitor: "竞品词",
        }[type] ||
        type ||
        "-"
      );
    },
    keywordRankStatus(row) {
      if (row.latest_rank_position) {
        return "found";
      }
      if (row.latest_checked_at) {
        return "not_found";
      }
      return "";
    },
    rankStatusText(status, row = {}) {
      if (status === "not_found") {
        return `前${row.depth || row.latest_rank_depth || 50}未找到`;
      }
      return { found: "已找到" }[status] || "-";
    },
    rankStatusTagType(status) {
      return { found: "success", not_found: "info" }[status] || "info";
    },
    formatCost(value) {
      return Number(value || 0).toFixed(6);
    },
    copyText(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message({ type: "success", message: "已复制" });
        });
        return;
      }
      const input = document.createElement("textarea");
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      this.$message({ type: "success", message: "已复制" });
    },
    committedRankLimitText(value) {
      const limit = Number(value || 30);
      return limit === 10 ? "前10" : limit === 50 ? "前50" : "前30";
    },
  },
};
</script>

<style lang="scss" scoped>
.seo-site-detail-page {
  min-height: calc(100vh - 84px);
  margin: -20px;
  padding: 24px;
  background: #f8fafc;
  color: #202124;

  &__header,
  &__info-panel,
  &__panel,
  &__stat-card {
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 22px;
  }

  &__title {
    margin-top: 12px;
    font-size: 22px;
    font-weight: 700;
    line-height: 30px;

    span {
      color: #5f6368;
      font-weight: 500;
    }
  }

  &__desc,
  &__panel-note,
  &__stat-extra {
    color: #5f6368;
    font-size: 13px;
    line-height: 20px;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 10px;
  }

  &__info-panel {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-top: 16px;
    padding: 16px;
  }

  &__info-item {
    span {
      display: block;
      color: #80868b;
      font-size: 12px;
      line-height: 18px;
    }

    strong {
      display: block;
      margin-top: 4px;
      color: #202124;
      font-size: 14px;
      line-height: 20px;
      word-break: break-word;
    }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px;
    margin-top: 16px;
  }

  &__stat-card {
    display: flex;
    gap: 12px;
    min-height: 108px;
    padding: 16px;
  }

  &__stat-icon {
    flex: 0 0 38px;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    border-radius: 8px;
    font-size: 18px;

    &--blue {
      background: #1a73e8;
    }

    &--green {
      background: #34a853;
    }

    &--yellow {
      background: #fbbc04;
      color: #202124;
    }

    &--purple {
      background: #9334e6;
    }

    &--red {
      background: #ea4335;
    }

    &--cyan {
      background: #12b5cb;
    }
  }

  &__stat-label {
    color: #5f6368;
    font-size: 13px;
    line-height: 18px;
  }

  &__stat-value {
    margin-top: 6px;
    font-size: 24px;
    font-weight: 700;
    line-height: 30px;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-top: 16px;
  }

  &__panel {
    margin-top: 16px;
    padding: 18px;
  }

  &__grid &__panel {
    margin-top: 0;
  }

  &__panel-head {
    margin-bottom: 12px;
  }

  &__panel-title {
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  &__chart {
    height: 310px;
  }

  &__pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }

  &__datetime {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    color: #5f6368;
    font-size: 12px;
    line-height: 16px;
  }

  &__ranking-url {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    span {
      flex: 1;
      overflow: hidden;
      color: #3c4043;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__copy-cell {
    display: block;
    width: 100%;
    padding: 0;
    overflow: hidden;
    color: #1a73e8;
    font: inherit;
    line-height: 20px;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    background: transparent;
    border: 0;

    &:hover {
      color: #174ea6;
      text-decoration: underline;
    }
  }

  &__url-popover {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  &__url-popover-text {
    flex: 1;
    max-height: 120px;
    overflow: auto;
    color: #202124;
    font-size: 12px;
    line-height: 18px;
    overflow-wrap: anywhere;
  }

  &__snapshot-drawer {
    padding: 0 18px 18px;
  }

  &__snapshot-title {
    margin-bottom: 14px;
    color: #202124;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  &__rank-up {
    color: #34a853;
    font-weight: 600;
  }

  &__rank-down {
    color: #ea4335;
    font-weight: 600;
  }

  ::v-deep .el-table th.el-table__cell {
    background: #f8fafc;
    color: #5f6368;
    font-weight: 600;
  }

  ::v-deep .seo-site-detail-page__keyword-table .el-table__cell {
    padding: 8px 0;
  }

  ::v-deep .seo-site-detail-page__keyword-table .cell {
    padding-left: 8px;
    padding-right: 8px;
  }
}

@media (max-width: 1440px) {
  .seo-site-detail-page {
    &__stats,
    &__info-panel {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
}

@media (max-width: 1100px) {
  .seo-site-detail-page {
    &__header {
      flex-direction: column;
    }

    &__grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>

<style lang="scss">
.el-tooltip__popper.is-light {
  max-width: 420px;
  max-height: 50px;
  padding: 7px 9px;
  color: #202124;
  font-size: 12px;
  line-height: 18px;
  background: #fff;
  border: 1px solid #dfe3eb;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(32, 33, 36, 0.12);
  word-break: break-all;
  white-space: normal;
  overflow: hidden;
}

.seo-site-detail-page__url-popper {
  max-width: 460px;
}
</style>
